/**
 * useAPI Hook
 * Handles API calls to the Dashboard API Server
 */

import { useCallback, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useDashboardStore } from '../store/dashboardStore';
import toast from 'react-hot-toast';

// Dynamically resolve the backend URL so it works seamlessly on any custom port or staging/production server.
// If the dashboard is served statically by the proxy/router, we should use the same origin!
const getDynamicUrls = () => {
  if (typeof window !== 'undefined') {
    // If the port is Vite's dev server port (e.g. 8082 or 5173 or 3000), use the environment variables
    const devPorts = ['8082', '5173', '3000'];
    if (devPorts.includes(window.location.port)) {
      return {
        apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:8081',
        wsUrl: import.meta.env.VITE_WS_URL || 'http://localhost:8081'
      };
    }
    // Otherwise, we are served statically on the same port as the backend API
    return {
      apiUrl: window.location.origin,
      wsUrl: window.location.origin
    };
  }
  return {
    apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:8081',
    wsUrl: import.meta.env.VITE_WS_URL || 'http://localhost:8081'
  };
};

const urls = getDynamicUrls();
const API_URL = urls.apiUrl;
const WS_URL = urls.wsUrl;

type UseAPIOptions = {
  enableSocket?: boolean;
};

const normalizeEventType = (type: string) => {
  const mapping: Record<string, string> = {
    'agent:joined': 'agent_join',
    'agent:left': 'agent_leave',
    'agent:error': 'error',
    'cache:hit': 'cache_hit',
    'cache:miss': 'cache_miss',
    'debugger:paused': 'debugger_paused',
    'debugger:resumed': 'debugger_resumed',
  };

  return mapping[type] || type.replace(/:/g, '_');
};

const normalizeEvent = (event: any) => ({
  ...event,
  type: normalizeEventType(event.type),
});

export function useAPI(options: UseAPIOptions = {}) {
  const store = useDashboardStore();
  const enableSocket = options.enableSocket ?? false;

  // WebSocket connection
  const socketRef = useRef<Socket | null>(null);

  // Initialize WebSocket connection
  useEffect(() => {
    if (!enableSocket) {
      return;
    }

    store.setConnectionStatus('connecting');

    try {
      socketRef.current = io(WS_URL, {
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
      });

      // Mark as connected immediately for REST-polling mode
      store.setConnected(true);
      store.setConnectionStatus('connected');

      socketRef.current.on('connect', () => {
        console.log('✓ Connected to Dashboard API via WebSocket');
        store.setConnected(true);
        store.setConnectionStatus('connected');
        socketRef.current?.emit('subscribe:metrics');
      });

      socketRef.current.on('connect_error', () => {
        // WS unavailable — REST polling keeps the UI live
        store.setConnected(true);
        store.setConnectionStatus('connected');
      });

      socketRef.current.on('metrics:update', (data) => {
        store.setMetrics({
          totalRequests: data.totalRequests,
          cacheHits: data.cacheHits,
          cacheMisses: data.cacheMisses,
          hitRate: parseFloat(data.hitRate),
          averageLatency: data.averageLatency,
          tokensSaved: data.tokensSaved,
          costSaved: data.costSaved,
          activeAgents: data.activeAgents || 0,
          totalAgents: data.totalAgents || 0,
          uptime: data.uptime || 0,
          timestamp: data.timestamp,
          tier: data.tier || 'free',
        });
      });

      socketRef.current.on('agent:joined', (event) => {
        console.log('Agent joined:', event);
        if (event.capabilities) {
          store.addAgent({
            id: event.agentId,
            name: event.agentName,
            status: 'active',
            capabilities: event.capabilities,
            tasksCompleted: 0,
            lastSeen: new Date().toISOString(),
            errorCount: 0,
            cacheHitRate: 0,
          });
        }
        store.addEvent(normalizeEvent(event));
      });

      socketRef.current.on('agent:left', (event) => {
        console.log('Agent left:', event);
        store.removeAgent(event.agentId);
        store.addEvent(normalizeEvent(event));
      });

      socketRef.current.on('agent:error', (event) => {
        console.log('Agent error:', event);
        store.addEvent(normalizeEvent(event));
        toast.error(`Agent ${event.agentId} error: ${event.errorMessage}`);
      });

      socketRef.current.on('cache:hit', (event) => {
        store.addEvent(normalizeEvent(event));
      });

      socketRef.current.on('cache:miss', (event) => {
        store.addEvent(normalizeEvent(event));
      });

      socketRef.current.on('debugger:paused', () => {
        store.setPaused(true);
      });

      socketRef.current.on('debugger:resumed', () => {
        store.setPaused(false);
      });

      socketRef.current.on('disconnect', () => {
        console.log('✗ Disconnected from Dashboard API');
        store.setConnected(false);
        store.setConnectionStatus('disconnected');
      });

      socketRef.current.on('error', (error) => {
        console.error('WebSocket error:', error);
        toast.error('WebSocket connection error');
      });
    } catch (error) {
      console.error('Failed to initialize WebSocket:', error);
      store.setConnectionStatus('disconnected');
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [enableSocket]);

  // Fetch metrics via REST API
  const fetchMetrics = useCallback(async () => {
    try {
      store.setMetricsLoading(true);
      const response = await fetch(`${API_URL}/api/metrics`);
      if (!response.ok) throw new Error('Failed to fetch metrics');
      
      const data = await response.json();
      store.setConnectionStatus('connected');
      store.setConnected(true);
      store.setMetrics({
        totalRequests: data.totalRequests,
        cacheHits: data.cacheHits,
        cacheMisses: data.cacheMisses,
        hitRate: parseFloat(data.hitRate),
        averageLatency: data.averageLatency,
        tokensSaved: data.tokensSaved,
        costSaved: data.costSaved,
        activeAgents: data.activeAgents,
        totalAgents: data.totalAgents,
        uptime: data.uptime || 0,
        timestamp: data.timestamp,
        tier: data.tier || 'free',
      });
    } catch (error: any) {
      console.error('Error fetching metrics:', error);
      store.setMetricsError(error.message);
    }
  }, []);

  // Fetch agents via REST API
  const fetchAgents = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/agents`);
      if (!response.ok) throw new Error('Failed to fetch agents');
      
      const data = await response.json();
      store.setAgents(data.agents);
    } catch (error: any) {
      console.error('Error fetching agents:', error);
      toast.error('Failed to fetch agents');
    }
  }, []);

  // Fetch events via REST API
  const fetchEvents = useCallback(async (page: number = 1, limit: number = 50) => {
    try {
      const response = await fetch(
        `${API_URL}/api/events?page=${page}&limit=${limit}`
      );
      if (!response.ok) throw new Error('Failed to fetch events');
      
      const data = await response.json();
      store.setEvents((data.events || []).map(normalizeEvent));
      store.setTotalEvents(data.pagination.total);
    } catch (error: any) {
      console.error('Error fetching events:', error);
      toast.error('Failed to fetch events');
    }
  }, []);

  // Fetch cache stats via REST API
  const fetchCacheStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/cache-stats`);
      if (!response.ok) throw new Error('Failed to fetch cache stats');
      
      const data = await response.json();
      store.setTopQueries(data.topQueries);
    } catch (error: any) {
      console.error('Error fetching cache stats:', error);
      toast.error('Failed to fetch cache stats');
    }
  }, []);

  // Fetch savings breakdown from SavingsLedger via REST API
  const fetchSavingsBreakdown = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/savings-breakdown`);
      if (!response.ok) return; // Non-critical, fail silently
      const data = await response.json();
      store.setSavingsBreakdown(data);
    } catch {
      // Non-critical — savings breakdown is optional UI enhancement
    }
  }, []);

  // Clear cache
  const clearCache = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/cache/clear`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to clear cache');
      
      const data = await response.json();
      toast.success(`Cleared ${data.clearedEntries} cache entries`);
      
      // Refresh metrics
      fetchMetrics();
    } catch (error: any) {
      console.error('Error clearing cache:', error);
      toast.error('Failed to clear cache');
    }
  }, []);

  // Pause debugger
  const pauseDebugger = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/debugger/pause`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to pause');
      
      store.setPaused(true);
      toast.success('Paused agent processing');
    } catch (error: any) {
      console.error('Error pausing:', error);
      toast.error('Failed to pause');
    }
  }, []);

  // Resume debugger
  const resumeDebugger = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/debugger/resume`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to resume');
      
      store.setPaused(false);
      toast.success('Resumed agent processing');
    } catch (error: any) {
      console.error('Error resuming:', error);
      toast.error('Failed to resume');
    }
  }, []);

  // Rewind to event
  const rewindToEvent = useCallback(async (eventId: string) => {
    try {
      const response = await fetch(`${API_URL}/api/debugger/rewind`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventId }),
      });
      if (!response.ok) throw new Error('Failed to rewind');
      
      const data = await response.json();
      store.setDebuggerState(data.state);
      toast.success('Rewound to selected event');
    } catch (error: any) {
      console.error('Error rewinding:', error);
      toast.error('Failed to rewind');
    }
  }, []);

  // Fetch search results
  const fetchSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      store.setSearchResults([]);
      return;
    }
    
    try {
      store.setIsSearching(true);
      const response = await fetch(`${API_URL}/api/search?q=${encodeURIComponent(query)}`);
      if (!response.ok) throw new Error('Search failed');
      
      const data = await response.json();
      store.setSearchResults(data.results || []);
    } catch (error: any) {
      console.error('Error during search:', error);
      toast.error('Search failed: ' + error.message);
      store.setIsSearching(false);
    }
  }, []);

  return {
    fetchMetrics,
    fetchAgents,
    fetchEvents,
    fetchCacheStats,
    fetchSavingsBreakdown,
    clearCache,
    pauseDebugger,
    resumeDebugger,
    rewindToEvent,
    fetchSearch,
  };
}
